/**
 * One watched subject: what is being checked, what it found, and what is wrong.
 *
 * THE PROBE CARDS SAY WHAT WILL ACTUALLY HAPPEN, not what was typed. A probe's
 * stored interval can be floored by its kind (Trends is weekly-granular, so
 * asking daily returns the same curve), and a screen that echoed the stored value
 * back would leave somebody certain the setting was ignored. The server sends
 * both numbers and a flag; this shows the effective one and explains the
 * difference.
 *
 * AND A PAUSED PROBE SAYS WHY. A monitor that has quietly stopped working must
 * never read the same as one that is working and finding nothing — that is the
 * worst ambiguity this product can have, so the reason is on the card and the
 * un-pause is beside it.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useWebSocket } from '../contexts/WebSocketContext';
import PageHeader from '../components/common/PageHeader';
import { OUTCOME_CLASSES, outcomeTone, tierTone } from '../components/watch/tierTone';
import VerdictPanel from '../components/watch/VerdictPanel';
import ProbeEditor from '../components/watch/ProbeEditor';
import KnobsPanel from '../components/watch/KnobsPanel';
import { engineOf, fieldsFor, missingFor } from '../components/watch/probeFields';
import StageModelsWidget from '../components/common/StageModelsWidget';
import {
  applyRunEvent,
  intervalLabel,
  nextCheckLabel,
  runEventChangedTheFeed,
  subjectStatus,
  type ProbeActivity,
} from './watchFeed';
import type {
  WatchCatalogue,
  WatchEvent,
  WatchProbe,
  WatchRun,
  WatchSubject as Subject,
} from '../types';
import EventDetails from '../components/watch/EventDetails';
import WatchTimeline from '../components/watch/WatchTimeline';
import { partialWarning, sinceLine } from '../components/watch/watchEventLines';

export default function WatchSubject() {
  const { subjectId } = useParams<{ subjectId: string }>();
  // Come from a check on the feed: its point on the timeline is picked out.
  const [searchParams] = useSearchParams();
  const focusRun = Number(searchParams.get('run')) || undefined;
  const id = Number(subjectId);

  const [subject, setSubject] = useState<Subject | null>(null);
  const [events, setEvents] = useState<WatchEvent[]>([]);
  const [runs, setRuns] = useState<Record<number, WatchRun[]>>({});
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // What each probe is doing right now, from the run notices. This is the only thing that moves
  // when a check finds nothing — which is most of the time.
  const [activity, setActivity] = useState<Record<number, ProbeActivity>>({});
  // Bumped whenever the page reloads, so the verdict is re-read after a check ran or a
  // setting changed rather than standing as advice about the state before it.
  const [reading, setReading] = useState(0);
  // Which check's form is open. One at a time: two open forms invite saving the wrong one.
  const [editing, setEditing] = useState<number | null>(null);
  // The check whose delete is waiting on a second click.
  const [deleting, setDeleting] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  // The catalogue says which alerts each knob governs, so the settings rows can be labelled by
  // what they DO rather than by their key. Loaded once — it does not change while you look at it.
  const [catalogue, setCatalogue] = useState<WatchCatalogue | null>(null);
  const { subscribe } = useWebSocket();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [detail, feed] = await Promise.all([
        api.getWatchSubject(id),
        api.getWatchFeed({ subjectId: id, limit: 30 }),
      ]);
      setSubject(detail);
      setEvents(feed.events);
      setReading((n) => n + 1);
      // Alongside rather than blocking: the settings panel is collapsed, so a slow catalogue
      // must not hold up the alerts.
      void api
        .getWatchCatalogue(detail.kind)
        .then(setCatalogue)
        .catch(() => undefined);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this watch');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (Number.isFinite(id)) void load();
  }, [id, load]);

  useEffect(
    () =>
      subscribe((event) => {
        if (event.event_type !== 'watch_run' || event.subject_id !== id) return;
        setActivity((current) => applyRunEvent(current, event));
        // Only reload when the check actually wrote something. A quiet finish is already fully
        // described by the notice, so refetching for it would be a round trip to change nothing.
        if (runEventChangedTheFeed(event)) void load();
      }),
    [subscribe, id, load],
  );

  const act = async (fn: () => Promise<unknown>, probeId: number | null = null) => {
    setBusy(probeId ?? -1);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work');
    } finally {
      setBusy(null);
    }
  };

  const showRuns = async (probe: WatchProbe) => {
    if (runs[probe.id]) {
      setRuns((current) => {
        const next = { ...current };
        delete next[probe.id];
        return next;
      });
      return;
    }
    setRuns((current) => ({ ...current, [probe.id]: [] }));
    const rows = await api.getWatchRuns(probe.id, 10);
    setRuns((current) => ({ ...current, [probe.id]: rows }));
  };

  // Timeline or list, remembered in this browser.
  const [view, setView] = useState<'timeline' | 'list'>(() => {
    if (focusRun) return 'timeline';
    try {
      return window.localStorage.getItem('jcseo.watch.view') === 'list' ? 'list' : 'timeline';
    } catch {
      return 'timeline';
    }
  });
  const pickView = (v: 'timeline' | 'list') => {
    setView(v);
    try {
      window.localStorage.setItem('jcseo.watch.view', v);
    } catch {
      /* private mode: per visit */
    }
  };
  // Which alerts are open onto their details.
  const [openEvents, setOpenEvents] = useState<Set<number>>(() => new Set());
  const toggleEvent = (eventId: number) =>
    setOpenEvents((cur) => {
      const next = new Set(cur);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });

  if (loading && !subject) return <div className="text-sm text-gray-500">Loading…</div>;
  if (!subject) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-600 dark:text-red-400">{error ?? 'Not found.'}</p>
        <Link to="/watch" className="text-sm text-indigo-600 hover:underline">
          Back to Watch
        </Link>
      </div>
    );
  }

  const status = subjectStatus(subject);
  const probes = subject.probes ?? [];
  // Optional on the type because the LIST endpoint does not send it — only the detail one does.
  const offerable = subject.offerable_probes ?? [];

  return (
    <div>
      {/* The way back is the breadcrumb, not a bespoke arrow. `NewsBeat` records why: a hand-rolled
          `← Watch` would be the fifth back-link style in this app, and PageHeader exists because
          every screen had invented its own answer to "where am I, and how do I get out". */}
      <PageHeader
        title={subject.name}
        breadcrumb={[{ label: 'Watch', to: '/watch' }, { label: subject.name }]}
        back
        className="mb-4"
        /* MOST CHECKS SPEND NO MODEL AT ALL — a sitemap diff and a page diff are
           arithmetic. The two jobs behind this widget run only on pages that publish
           no structured data, which is exactly where a model is the last resort. The
           list is the backend's (stage_surfaces."watch_subject"), so a third rung
           added later shows up here without a frontend change. */
        right={<StageModelsWidget surface="watch_subject" />}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              {subject.kind.toLowerCase().replace('_', ' ')}
            </span>
            {subject.mode === 'DRAFT' && (
              <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                not watching yet
              </span>
            )}
            {subject.home_url && (
              <a
                href={subject.home_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs underline text-gray-500 dark:text-gray-400"
              >
                {subject.domain_key ?? subject.home_url}
              </a>
            )}
            <span className={`text-xs ${OUTCOME_CLASSES[status.tone === 'problem' ? 'bad' : 'quiet']}`}>
              {status.text}
            </span>
          </span>
        }
      />

      {subject.boardroom_id && (
        <p className="mb-4 text-[11px] text-gray-500 dark:text-gray-400">
          Started from{' '}
          <Link to={`/boardroom/${subject.boardroom_id}`} className="underline">
            a recon
          </Link>
          . Archiving that room leaves this watch running.
        </p>
      )}

      {error && (
        <p className="mb-4 text-sm text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}

      <VerdictPanel subjectId={subject.id} refreshKey={reading} />

      {/* Directly under the verdict, and that is the point: the verdict names these knobs by
          their keys, and until now there was nowhere to type the number it suggested. */}
      <KnobsPanel subject={subject} catalogue={catalogue} onSaved={() => void load()} />

      {subject.unknown_threshold_keys.length > 0 && (
        <p className="mb-4 rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          These settings match no signal and are doing nothing:{' '}
          {subject.unknown_threshold_keys.join(', ')}.
        </p>
      )}

      <section className="mb-6">
        <div className="mb-2 flex items-baseline justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            What is checked
          </h2>
          <div className="flex items-center gap-2">
          {offerable.some((k) => !probes.some((p) => p.kind === k)) && (
            <button
              type="button"
              onClick={() => setAdding((v) => !v)}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
            >
              {adding ? 'Never mind' : 'Add a check'}
            </button>
          )}
          {subject.mode === 'DRAFT' ? (
            <button
              type="button"
              disabled={busy !== null || !probes.some((p) => p.is_active && !p.paused_reason)}
              onClick={() => act(() => api.watchSubjectGoLive(subject.id))}
              className="text-xs px-2 py-1 rounded bg-gray-900 text-white disabled:opacity-40 dark:bg-gray-100 dark:text-gray-900"
              title="Starts the cadence. Needs at least one active check."
            >
              Start watching
            </button>
          ) : (
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => act(() => api.watchSubjectToDraft(subject.id))}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-600"
              title="Takes it out of the schedule. Nothing already found is deleted."
            >
              Pause everything
            </button>
          )}
          </div>
        </div>

        {adding && (
          <ul className="mb-3 space-y-1 rounded border border-gray-200 p-2 dark:border-gray-700">
            {offerable
              .filter((k) => !probes.some((p) => p.kind === k))
              .map((kind) => (
                <li key={kind}>
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() =>
                      act(async () => {
                        const made = await api.createWatchProbe(subject.id, { kind });
                        // Straight into its form when it needs pointing at something, so a
                        // check is never left aimed at nothing — which is the state that used
                        // to refuse silently once a day forever.
                        if (fieldsFor(kind).length) setEditing(made.id);
                        setAdding(false);
                      })
                    }
                    className="w-full rounded px-2 py-1 text-left text-xs hover:bg-gray-50 disabled:opacity-40 dark:hover:bg-gray-800"
                  >
                    {kind.replace(/_/g, ' ')}
                  </button>
                </li>
              ))}
          </ul>
        )}

        <ul className="space-y-2">
          {probes.map((probe) => (
            <li
              key={probe.id}
              className="rounded border border-gray-200 dark:border-gray-700 p-3"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {probe.label || probe.title}
                    {probe.target && (
                      <span className="ml-2 text-[11px] font-normal text-gray-500 dark:text-gray-400">
                        {probe.target}
                      </span>
                    )}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400">{probe.looks_at}</p>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => act(() => api.runWatchProbe(probe.id), probe.id)}
                    className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40"
                  >
                    Check now
                  </button>
                  {fieldsFor(probe.kind).length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setEditing((id) => (id === probe.id ? null : probe.id))
                      }
                      className="underline text-gray-500 dark:text-gray-400"
                    >
                      {editing === probe.id ? 'Close' : 'Settings'}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void showRuns(probe)}
                    className="underline text-gray-500 dark:text-gray-400"
                  >
                    {runs[probe.id] ? 'Hide history' : 'History'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting((id) => (id === probe.id ? null : probe.id))}
                    className="underline text-gray-500 hover:text-red-700 dark:text-gray-400 dark:hover:text-red-300"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* Asked here rather than in a browser dialog. Its runs and readings go;
                  the alerts it raised keep their place on the feed. */}
              {deleting === probe.id && (
                <p
                  className="mt-1 text-[11px] text-red-700 dark:text-red-300"
                  data-testid={`watch-probe-delete-${probe.id}`}
                >
                  Delete this check and its history? Alerts it already raised stay on the feed.{' '}
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() =>
                      act(async () => {
                        await api.deleteWatchProbe(probe.id);
                        setDeleting(null);
                        if (editing === probe.id) setEditing(null);
                      }, probe.id)
                    }
                    className="ml-1 rounded bg-red-600 px-2 py-0.5 text-white disabled:opacity-40"
                  >
                    Delete
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(null)}
                    className="ml-2 underline text-gray-500 dark:text-gray-400"
                  >
                    Keep it
                  </button>
                </p>
              )}

              {/* A CHECK AIMED AT NOTHING LOOKS EXACTLY LIKE A CHECK FINDING NOTHING, and only one
                  of those is fine. This is the line that tells them apart. */}
              {missingFor(probe) && editing !== probe.id && (
                <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
                  {missingFor(probe)}{' '}
                  <button
                    type="button"
                    onClick={() => setEditing(probe.id)}
                    className="underline"
                  >
                    Set it
                  </button>
                </p>
              )}

              {editing === probe.id && (
                <ProbeEditor
                  probe={probe}
                  onSaved={() => {
                    setEditing(null);
                    void load();
                  }}
                  onCancel={() => setEditing(null)}
                />
              )}

              {activity[probe.id] && (
                <p
                  className={`mt-1 text-[11px] ${
                    activity[probe.id].done
                      ? 'text-gray-600 dark:text-gray-300'
                      : 'text-blue-600 dark:text-blue-300'
                  }`}
                  data-testid={`probe-activity-${probe.id}`}
                >
                  {activity[probe.id].done ? 'Last check: ' : ''}
                  {activity[probe.id].text}
                  {activity[probe.id].found > 0 &&
                    ` - ${activity[probe.id].found} change${
                      activity[probe.id].found === 1 ? '' : 's'
                    }`}
                </p>
              )}
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                {intervalLabel(probe.effective_interval_minutes)}
                {/* Say why, or somebody is certain their setting was ignored. */}
                {probe.interval_floored && (
                  <span className="text-amber-700 dark:text-amber-400">
                    {' '}
 - the most often this can usefully run
                  </span>
                )}
                {engineOf(probe) && <> · searches on {engineOf(probe)}</>}
                {nextCheckLabel(probe) && <> · next {nextCheckLabel(probe)}</>}
                {!probe.is_baselined && (
                  <> · first look not taken yet, so nothing is being compared</>
                )}
              </p>

              {probe.paused_reason && (
                <p className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-red-700 dark:text-red-300">
                  Stopped: {probe.paused_reason}
                  <button
                    type="button"
                    disabled={busy !== null}
                    onClick={() => act(() => api.patchWatchProbe(probe.id, { unpause: true }), probe.id)}
                    className="px-2 py-0.5 rounded border border-red-300 dark:border-red-700"
                  >
                    Try again
                  </button>
                </p>
              )}

              {runs[probe.id] && (
                <ul className="mt-2 space-y-1 border-t border-gray-100 dark:border-gray-800 pt-2">
                  {!runs[probe.id].length && (
                    <li className="text-[11px] text-gray-400">No checks yet.</li>
                  )}
                  {runs[probe.id].map((run) => (
                    <li key={run.id} className="flex items-baseline gap-2 text-[11px]">
                      <span className={OUTCOME_CLASSES[outcomeTone(run.ended_reason, run.status)]}>
                        {run.outcome}
                      </span>
                      <span className="text-gray-400">
                        {run.ended_at ? new Date(run.ended_at).toLocaleString() : 'running'}
                      </span>
                      {run.events_written > 0 && (
                        <span className="text-gray-500">
                          {run.events_written} change{run.events_written === 1 ? '' : 's'}
                        </span>
                      )}
                      {run.last_error && (
                        <span className="text-red-600 dark:text-red-400 truncate">
                          {run.last_error}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
          {!probes.length && (
            <li className="rounded border border-dashed border-gray-300 dark:border-gray-700 p-4 text-center text-xs text-gray-500 dark:text-gray-400">
              Nothing is being checked yet.
            </li>
          )}
        </ul>
      </section>

      {subject.members && subject.members.length > 0 && (
        <section className="mb-6">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            In this market
          </h2>
          <ul className="flex flex-wrap gap-1 text-[11px]">
            {subject.members.map((member) => (
              <li key={member.id}>
                <Link
                  to={`/watch/subjects/${member.id}`}
                  className="px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  {member.name}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <div className="mb-2 flex items-center gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            What changed
          </h2>
          {/* A TIMELINE OF CHECKS, not a pile of alerts: one point per check,
              quiet stretches folded, failed and partial checks said. The list of
              alerts stays a click away. */}
          <span className="ml-auto flex gap-1 text-xs" role="tablist">
            {(['timeline', 'list'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                onClick={() => pickView(v)}
                className={`rounded px-2 py-0.5 ${
                  view === v
                    ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                    : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
                data-testid={`watch-view-${v}`}
              >
                {v === 'timeline' ? 'Timeline' : 'List'}
              </button>
            ))}
          </span>
        </div>
        {view === 'timeline' ? (
          <WatchTimeline
            subjectId={subject.id}
            refreshKey={events.length + ':' + (events[0]?.id ?? '')}
            focusRun={focusRun}
          />
        ) : (
        <ul className="space-y-2">
          {events.map((event) => {
            const tone = tierTone(event.tier);
            const isOpen = openEvents.has(event.id);
            return (
              <li key={event.id} className="rounded border border-gray-200 dark:border-gray-700 p-3">
                {/* THE ROW OPENS ONTO WHAT IT FOUND: the pages, the search, the
                    positions - recorded on the alert and never shown. */}
                <button
                  type="button"
                  onClick={() => toggleEvent(event.id)}
                  aria-expanded={isOpen}
                  className="block w-full text-left"
                  data-testid={`watch-event-${event.id}`}
                >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tone.chip}`}
                    title={tone.hint}
                  >
                    {tone.label}
                  </span>
                  <span className="text-[11px] text-gray-400">{event.family_title}</span>
                  <span className="text-[11px] text-gray-400">
                    {event.detected_at ? new Date(event.detected_at).toLocaleString() : ''}
                  </span>
                </div>
                <p className="mt-1 text-sm font-medium">{event.headline}</p>
                {event.so_what && (
                  <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">{event.so_what}</p>
                )}
                {/* SINCE WHEN, FROM WHAT: on the row, not only in the details. */}
                {partialWarning(event) && (
                  <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">{partialWarning(event)}</p>
                )}
                {sinceLine(event) && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400" data-testid={`watch-since-${event.id}`}>
                    {sinceLine(event)}
                  </p>
                )}
                <span className="mt-1 inline-block text-[11px] text-indigo-600 dark:text-indigo-300">
                  {isOpen ? 'Hide details ▾' : 'Details ▸'}
                </span>
                </button>
                {isOpen && <EventDetails event={event} />}
              </li>
            );
          })}
          {!events.length && (
            <li className="text-xs text-gray-500 dark:text-gray-400">
              Nothing yet. The first look at something records what is there now and reports
              nothing - changes come after that.
            </li>
          )}
        </ul>
        )}
      </section>
    </div>
  );
}
